import React, { useState, useEffect, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  Chip,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { ColumnDef, ColumnId, DEFAULT_COLUMNS } from '../hooks/useTableColumns';

interface ColumnConfigDialogProps {
  open: boolean;
  onClose: () => void;
  columns: ColumnDef[];
  onSave: (newColumns: ColumnDef[]) => void;
  onResetToDefaults: () => void;
  shouldShowIIN?: boolean;
}

const ColumnConfigDialog: React.FC<ColumnConfigDialogProps> = memo(({
  open,
  onClose,
  columns,
  onSave,
  onResetToDefaults,
  shouldShowIIN = true
}) => {
  const [draft, setDraft] = useState<ColumnDef[]>(columns);

  // Синхронизация при открытии диалога
  useEffect(() => {
    if (open) {
      setDraft(columns);
    }
  }, [open, columns]);

  // Переключение видимости (только для незафиксированных колонок)
  const handleToggle = (id: ColumnId) => {
    setDraft(prev =>
      prev.map(col => {
        if (col.fixed) return col;
        if (col.id === id) {
          return { ...col, visible: !col.visible };
        }
        return col;
      })
    );
  };

  // Перемещение колонки вверх (только в диапазоне [2, length - 2])
  const handleMoveUp = (index: number) => {
    if (index <= 2) return; // Нельзя зайти на место fixed: start_1 (user) или start_2 (role)
    setDraft(prev => {
      const next = [...prev];
      const temp = next[index];
      next[index] = next[index - 1];
      next[index - 1] = temp;
      return next;
    });
  };

  // Перемещение колонки вниз (только в диапазоне [2, length - 2])
  const handleMoveDown = (index: number) => {
    if (index >= draft.length - 2) return; // Нельзя зайти на место fixed: end (actions)
    setDraft(prev => {
      const next = [...prev];
      const temp = next[index];
      next[index] = next[index + 1];
      next[index + 1] = temp;
      return next;
    });
  };

  // Сброс к заводским настройкам
  const handleReset = () => {
    setDraft(DEFAULT_COLUMNS.map(c => ({ ...c })));
    onResetToDefaults();
  };

  // Применение изменений
  const handleApply = () => {
    onSave(draft);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }
      }}
    >
      {/* Заголовок */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#f8fafc'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: '#eff6ff',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #dbeafe'
            }}
          >
            <ViewColumnOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              Настройка колонок таблицы
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Управляйте видимостью и порядком отображения полей
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            color: '#94a3b8',
            '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' }
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      {/* Основное содержимое */}
      <DialogContent sx={{ p: 2.5, bgcolor: '#ffffff' }}>
        <Alert
          severity="info"
          icon={<LockOutlinedIcon fontSize="inherit" />}
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: '#eff6ff',
            color: '#1e40af',
            border: '1px solid #dbeafe',
            '& .MuiAlert-icon': { color: '#2563eb' }
          }}
        >
          Поля <strong>«Пользователь»</strong> и <strong>«Роль»</strong> всегда первыми, а <strong>«Быстрый доступ»</strong> - всегда последним. Остальные поля можно настраивать, скрывать и менять местами.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {draft.map((col, index) => {
            if (!shouldShowIIN && col.id === 'iin') return null;
            const isFixed = Boolean(col.fixed);
            const isFirstConfigurable = index === 2;
            const isLastConfigurable = index === draft.length - 2;

            return (
              <Box
                key={col.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '8px 12px',
                  borderRadius: 2,
                  border: isFixed ? '1px dashed #cbd5e1' : '1px solid #e2e8f0',
                  bgcolor: isFixed ? '#f8fafc' : col.visible ? '#ffffff' : '#f8fafc',
                  opacity: !isFixed && !col.visible ? 0.65 : 1,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isFixed ? '#f8fafc' : '#f1f5f9',
                    borderColor: isFixed ? '#cbd5e1' : '#cbd5e1'
                  }
                }}
              >
                {/* Чекбокс видимости */}
                <Checkbox
                  checked={isFixed ? true : col.visible}
                  disabled={isFixed}
                  onChange={() => handleToggle(col.id)}
                  size="small"
                  sx={{
                    p: 0.5,
                    color: '#94a3b8',
                    '&.Mui-checked': {
                      color: isFixed ? '#64748b' : '#2563eb'
                    }
                  }}
                />

                {/* Название и описание */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isFixed ? '#475569' : '#1e293b',
                        fontSize: '0.875rem'
                      }}
                    >
                      {col.label}
                    </Typography>
                    {col.fixed === 'start_1' && (
                      <Chip
                        label="Всегда 1-я"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: '#f1f5f9',
                          color: '#475569',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                    )}
                    {col.fixed === 'start_2' && (
                      <Chip
                        label="Всегда 2-я"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: '#f1f5f9',
                          color: '#475569',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                    )}
                    {col.fixed === 'end' && (
                      <Chip
                        label="Всегда в конце"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: '#f1f5f9',
                          color: '#475569',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                    )}
                  </Box>
                  {col.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        display: 'block',
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        mt: 0.25
                      }}
                    >
                      {col.description}
                    </Typography>
                  )}
                </Box>

                {/* Кнопки перемещения для настраиваемых колонок */}
                {!isFixed ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={isFirstConfigurable ? "Колонка уже в самом верху списка" : "Переместить выше"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isFirstConfigurable}
                          onClick={() => handleMoveUp(index)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#ffffff',
                            color: '#475569',
                            '&:hover': { bgcolor: '#eff6ff', color: '#2563eb', borderColor: '#bfdbfe' },
                            '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1', borderColor: '#f1f5f9' }
                          }}
                        >
                          <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={isLastConfigurable ? "Колонка уже в самом низу списка" : "Переместить ниже"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isLastConfigurable}
                          onClick={() => handleMoveDown(index)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#ffffff',
                            color: '#475569',
                            '&:hover': { bgcolor: '#eff6ff', color: '#2563eb', borderColor: '#bfdbfe' },
                            '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1', borderColor: '#f1f5f9' }
                          }}
                        >
                          <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box sx={{ pr: 1 }}>
                    <LockOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      {/* Кнопки действий */}
      <DialogActions
        sx={{
          p: 2,
          px: 2.5,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Button
          variant="text"
          size="small"
          onClick={handleReset}
          startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            color: '#64748b',
            fontWeight: 600,
            fontSize: '0.8rem',
            '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a' }
          }}
        >
          По умолчанию
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderRadius: '8px',
              borderColor: '#cbd5e1',
              color: '#475569',
              '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
            }}
          >
            Отмена
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            startIcon={<CheckIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderRadius: '8px',
              bgcolor: '#1570ef',
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
              '&:hover': { bgcolor: '#175cd3' }
            }}
          >
            Применить
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
});

ColumnConfigDialog.displayName = 'ColumnConfigDialog';

export default ColumnConfigDialog;
